/**
 * Canonical Canvas Function / Generation Card Header (P0).
 *
 * LEFT:  [icon] [title]  (+ optional runtime status only)
 * RIGHT: existing per-node actions (History / Duplicate / Download / Delete / …)
 *
 * NEVER render in the header:
 * - version badges (v1.0)
 * - capability / node description
 * - subtitle / help copy
 * - provider / skill / model names
 * - upstream context (script / creative) — those belong in BODY
 */

import type { ReactNode } from 'react'

export type CanvasFunctionCardHeaderVariant = 'brief' | 'media'

export type CanvasFunctionCardHeaderProps = {
  icon: ReactNode
  color: string
  title: string
  /** Optional compact runtime status only (失败 / 生成中 / 需重新生成). Not version. */
  statusBadge?: ReactNode
  actions?: ReactNode
  /** Extra class on the header root (e.g. pcv-gen-image-header). */
  className?: string
  /**
   * `brief` — majority of generation cards (pcv-brief-header).
   * `media` — video-style media header (pcv-media-header).
   */
  variant?: CanvasFunctionCardHeaderVariant
  /** test id for E2E / contract checks */
  'data-testid'?: string
}

export default function CanvasFunctionCardHeader({
  icon,
  color,
  title,
  statusBadge,
  actions,
  className,
  variant = 'brief',
  'data-testid': testId = 'canvas-function-card-header',
}: CanvasFunctionCardHeaderProps) {
  if (variant === 'media') {
    return (
      <div
        className={`pcv-media-header pcv-function-card-header${className ? ` ${className}` : ''}`}
        data-testid={testId}
        data-header-contract="icon-title-actions"
      >
        <span className="pcv-media-icon" style={{ color, background: `${color}22` }}>
          {icon}
        </span>
        <span className="pcv-media-title" title={title}>
          {title}
        </span>
        {statusBadge}
        {actions ? (
          <div className="pcv-media-hover-toolbar pcv-gen-header-actions nodrag" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={`pcv-brief-header pcv-gen-creative-header pcv-function-card-header${className ? ` ${className}` : ''}`}
      data-testid={testId}
      data-header-contract="icon-title-actions"
    >
      <span className="pcv-brief-header-icon" style={{ color, background: `${color}22` }}>
        {icon}
      </span>
      <div className="pcv-gen-header-text">
        <div className="pcv-gen-header-title-row">
          <span className="pcv-brief-header-title" title={title}>
            {title}
          </span>
          {statusBadge}
        </div>
      </div>
      {actions ? (
        <div className="pcv-media-hover-toolbar pcv-gen-header-actions nodrag" onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      ) : null}
    </div>
  )
}
